import { useCallback, useMemo } from "react";
import hash from "object-hash";

import { Confidence, Mode, Source } from "@protos/common/v1/common";
import { IssuesFilter as IssuesFilterProto } from "@protos/issues/v1/issues";
import { SAST_FINDINGS_PAGE } from "@shared/constants";
import { useStateParams } from "@shared/hooks";

import { useCommonIssuesFilterState } from "./useCommonIssuesFilterState";

function confidenceToQueryParam(confidence: Confidence): string {
  switch (confidence) {
    case Confidence.HIGH:
      return "high";
    case Confidence.MEDIUM:
      return "medium";
    case Confidence.LOW:
      return "low";
    default:
      return "unspecified";
  }
}
function confidenceFromQueryParam(queryParam: string): Confidence {
  switch (queryParam) {
    case "high":
      return Confidence.HIGH;
    case "medium":
      return Confidence.MEDIUM;
    case "low":
      return Confidence.LOW;
    default:
      return Confidence.UNSPECIFIED;
  }
}

function modeToQueryParam(mode: Mode): string {
  switch (mode) {
    case Mode.BLOCK:
      return "block";
    case Mode.MONITOR:
      return "monitor";
    case Mode.COMMENT:
      return "comment";
    default:
      return "unspecified";
  }
}

function modeFromQueryParam(queryParam: string): Mode {
  switch (queryParam) {
    case "block":
      return Mode.BLOCK;
    case "monitor":
      return Mode.MONITOR;
    case "comment":
      return Mode.COMMENT;
    default:
      return Mode.UNSPECIFIED;
  }
}

function SourceToQueryParam(source: Source): string {
  switch (source) {
    case Source.PRO:
      return "pro";
    case Source.COMMUNITY:
      return "community";
    default:
      return "unspecified";
  }
}

function SourceFromQueryParam(queryParam: string): Source {
  switch (queryParam) {
    case "pro":
      return Source.PRO;
    case "community":
      return Source.COMMUNITY;
    default:
      return Source.UNSPECIFIED;
  }
}

/**
 * Generates and manages state for any finding filters which apply to SAST issues.
 */
export const useSastIssuesFilterState = () => {
  // grab the filters which apply to all issue types ("common" filters)
  const commonFilters = useCommonIssuesFilterState(SAST_FINDINGS_PAGE);

  // set up the state for the various SAST filters
  const [actions, setActions] = useStateParams<IssuesFilterProto["actions"]>(
    [],
    "action_type",
    (actionVals) => actionVals.map(modeToQueryParam).join(","),
    (actionStr) => actionStr.split(",").map(modeFromQueryParam),
    SAST_FINDINGS_PAGE
  );

  const [policies, setPolicies] = useStateParams<
    IssuesFilterProto["policySlugs"]
  >(
    [],
    "policy_slug",
    (p) => p.join(","),
    (s) => (s ? s.split(",") : []),
    SAST_FINDINGS_PAGE
  );

  const [confidence, setConfidence] = useStateParams<
    IssuesFilterProto["confidences"]
  >(
    [],
    "confidence",
    (confidenceVals) => confidenceVals.map(confidenceToQueryParam).join(","),
    (confidenceStr) => confidenceStr.split(",").map(confidenceFromQueryParam),
    SAST_FINDINGS_PAGE
  );

  const [categories, setCategories] = useStateParams<
    IssuesFilterProto["categories"]
  >(
    [],
    "category",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    SAST_FINDINGS_PAGE
  );

  const [rule, setRule] = useStateParams<IssuesFilterProto["rules"]>(
    [],
    "rule",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    SAST_FINDINGS_PAGE
  );

  const [ruleset, setRuleset] = useStateParams<IssuesFilterProto["rulesets"]>(
    [],
    "ruleset",
    (s) => s.join(","),
    (s) => (s ? s.split(",") : []),
    SAST_FINDINGS_PAGE
  );

  const [source, setSource] = useStateParams<IssuesFilterProto["source"]>(
    [],
    "source",
    (sourceVals) => sourceVals.map(SourceToQueryParam).join(","),
    (sourceStr) => sourceStr.split(",").map(SourceFromQueryParam),
    SAST_FINDINGS_PAGE
  );

  /**
   * A repackaged version of this state that is consumable by the issues API endpoint.
   */
  const issuesFilterProto = useMemo<IssuesFilterProto>(() => {
    return {
      ...commonFilters.issuesFilterProto,
      actions: actions,
      confidences: confidence,
      policySlugs: policies,
      rules: rule,
      rulesets: ruleset,
      categories: categories,
      source: source,
    };
  }, [
    actions,
    commonFilters.issuesFilterProto,
    categories,
    confidence,
    policies,
    rule,
    ruleset,
    source,
  ]);

  /**
   * A hash of all the filters in this object. Useful to detect when any filters have changed
   * without needing to understand the internal state of this hook.
   */
  const filterHash = useMemo(
    () =>
      hash([
        commonFilters.filterHash,
        actions,
        categories,
        confidence,
        policies,
        rule,
        ruleset,
        source,
      ]),
    [
      actions,
      commonFilters.filterHash,
      categories,
      confidence,
      policies,
      rule,
      ruleset,
      source,
    ]
  );

  /**
   * `True` if any filter field is set to to non-default values, `False` otherwise.
   */
  const hasFiltersSet =
    commonFilters.hasFiltersSet ||
    actions.length > 0 ||
    policies.length > 0 ||
    confidence.length > 0 ||
    categories.length > 0 ||
    rule.length > 0 ||
    ruleset.length > 0 ||
    source.length > 0;

  /**
   * Clears all filter fields, even those which have values by default
   */
  const clearFilters = useCallback(() => {
    commonFilters.clearFilters();
    setActions([]);
    setPolicies([]);
    setConfidence([]);
    setCategories([]);
    setRule([]);
    setRuleset([]);
    setSource([]);
  }, [
    commonFilters,
    setActions,
    setCategories,
    setConfidence,
    setPolicies,
    setRule,
    setRuleset,
    setSource,
  ]);

  /**
   * Resets all filter fields to their default values (empty, nullish, etc).
   */
  const resetFilters = useCallback(() => {
    commonFilters.resetFilters();
    setActions([]);
    setPolicies([]);
    setConfidence([]);
    setCategories([]);
    setRule([]);
    setRuleset([]);
    setSource([]);
  }, [
    commonFilters,
    setActions,
    setCategories,
    setConfidence,
    setPolicies,
    setRule,
    setRuleset,
    setSource,
  ]);

  return useMemo(
    () => ({
      issuesFilterProto,
      repo: commonFilters.repo,
      setRepo: commonFilters.setRepo,
      repoTag: commonFilters.repoTag,
      setRepoTag: commonFilters.setRepoTag,
      team: commonFilters.team,
      setTeam: commonFilters.setTeam,
      repoRef: commonFilters.repoRef,
      setRepoRef: commonFilters.setRepoRef,
      tab: commonFilters.tab,
      setTab: commonFilters.setTab,
      severities: commonFilters.severities,
      setSeverities: commonFilters.setSeverities,
      aiVerdicts: commonFilters.aiVerdicts,
      setAiVerdicts: commonFilters.setAiVerdicts,
      aiSensitivity: commonFilters.aiSensitivity,
      setAiSensitivity: commonFilters.setAiSensitivity,
      ref: commonFilters.ref,
      setRef: commonFilters.setRef,
      primary: commonFilters.primary,
      setPrimary: commonFilters.setPrimary,
      actions,
      setActions,
      policies,
      setPolicies,
      confidence,
      setConfidence,
      categories,
      setCategories,
      rule,
      setRule,
      ruleset,
      setRuleset,
      source,
      setSource,
      clearFilters,
      resetFilters,
      hasFiltersSet,
      filterHash,
    }),
    [
      actions,
      commonFilters.aiSensitivity,
      commonFilters.aiVerdicts,
      commonFilters.ref,
      commonFilters.repo,
      commonFilters.repoRef,
      commonFilters.repoTag,
      commonFilters.setAiSensitivity,
      commonFilters.setAiVerdicts,
      commonFilters.setRef,
      commonFilters.setRepo,
      commonFilters.setRepoRef,
      commonFilters.setRepoTag,
      commonFilters.setTab,
      commonFilters.setSeverities,
      commonFilters.setTeam,
      commonFilters.severities,
      commonFilters.tab,
      commonFilters.team,
      commonFilters.primary,
      commonFilters.setPrimary,
      categories,
      confidence,
      filterHash,
      hasFiltersSet,
      issuesFilterProto,
      policies,
      clearFilters,
      resetFilters,
      rule,
      ruleset,
      source,
      setActions,
      setCategories,
      setConfidence,
      setPolicies,
      setRule,
      setRuleset,
      setSource,
    ]
  );
};
